import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Gem, Crown, Check, Lock, Sparkles, Trophy, Coins, DollarSign, ArrowRight } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TIER_STYLE = {
  Bronze: "from-amber-700 to-amber-900",
  Silver: "from-slate-300 to-slate-500",
  Gold: "from-yellow-400 to-amber-600",
  Platinum: "from-cyan-200 to-slate-400",
  Diamond: "from-amber-300 via-fuchsia-400 to-cyan-300",
};

export default function VipLounge() {
  const { user } = useAuth();
  const [loy, setLoy] = useState(null);
  const [vipEvents, setVipEvents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: l }, { data: ts }] = await Promise.all([api.get("/loyalty"), api.get("/tournaments")]);
        setLoy(l);
        setVipEvents(ts.filter((t) => t.vip));
      } catch (e) { toast.error(apiErr(e)); }
    })();
  }, []);

  const isDiamond = (loy?.tier_index ?? 0) >= 4;
  const diamondTier = loy?.tiers?.find((t) => t.name === "Diamond");
  const toDiamond = Math.max(0, 15000 - (loy?.points ?? 0));
  const pct = Math.min(100, Math.round(((loy?.points ?? 0) / 15000) * 100));

  return (
    <div className="p-4 lg:p-8" data-testid="vip-lounge-page">
      {/* Hero */}
      <div className="relative rounded-3xl overflow-hidden mb-8 border border-amber-400/30 shadow-[0_0_40px_rgba(251,191,36,0.15)]">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/15 via-fuchsia-600/10 to-cyan-500/10" />
        <div className="relative z-10 p-6 lg:p-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-200 text-xs font-semibold mb-4">
            <Gem className="w-3.5 h-3.5" /> Diamond VIP Lounge
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3">
            <span className="bg-gradient-to-r from-amber-300 via-fuchsia-400 to-cyan-300 bg-clip-text text-transparent">The Diamond Lounge</span>
          </h1>
          <p className="text-gray-300 max-w-xl">Reach Diamond tier to unlock exclusive high-stakes events, bigger daily bonuses, and priority perks. Every game you play earns loyalty points.</p>
        </div>
      </div>

      {/* Current status */}
      <div data-testid="vip-status-card" className="glass rounded-2xl border border-white/10 p-6 mb-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${TIER_STYLE[loy?.tier] || "from-gray-600 to-gray-800"} flex items-center justify-center`}>
              <Crown className="w-6 h-6 text-white drop-shadow" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Your tier</p>
              <p className="font-display text-2xl font-black" data-testid="vip-current-tier">{loy?.tier || "—"}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Loyalty points</p>
            <p className="font-mono font-black text-xl text-amber-300" data-testid="vip-points">{(loy?.points ?? 0).toLocaleString()}</p>
          </div>
        </div>
        {isDiamond ? (
          <div className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-400/15 to-fuchsia-500/15 border border-amber-400/40 px-4 py-3">
            <Sparkles className="w-5 h-5 text-amber-300" />
            <p className="text-sm font-semibold text-amber-100">You're a Diamond VIP — all lounge perks and exclusive events are unlocked. 💎</p>
          </div>
        ) : (
          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1.5">
              <span>Progress to Diamond</span>
              <span className="font-semibold text-amber-300" data-testid="vip-to-diamond">{toDiamond.toLocaleString()} points to go</span>
            </div>
            <div className="w-full h-3 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-fuchsia-400 to-amber-300 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-xs text-gray-500 mt-2">Earn points by entering Fun Coin duels & tournaments, claiming your daily bonus, and playing daily challenges.</p>
          </div>
        )}
      </div>

      {/* Diamond perks */}
      <h2 className="font-display text-lg md:text-lg font-bold mb-4 flex items-center gap-2"><Gem className="w-5 h-5 text-amber-300" /> Diamond perks</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
        {(diamondTier?.perks || []).map((p, i) => (
          <div key={i} data-testid={`vip-perk-${i}`} className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${isDiamond ? "bg-amber-400/10 border-amber-400/30" : "bg-white/5 border-white/10"}`}>
            {isDiamond ? <Check className="w-5 h-5 text-amber-300 shrink-0" /> : <Lock className="w-4 h-4 text-gray-500 shrink-0" />}
            <span className={`text-sm ${isDiamond ? "text-amber-100" : "text-gray-400"}`}>{p}</span>
          </div>
        ))}
      </div>

      {/* VIP events */}
      <h2 className="font-display text-lg md:text-lg font-bold mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-300" /> Exclusive VIP events</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        {vipEvents.map((t) => {
          const coins = t.currency === "coins";
          return (
            <motion.div key={t.tournament_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              data-testid="vip-event-card" className="glass rounded-2xl border border-amber-400/30 p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 flex items-center gap-1.5 px-3 py-1.5 rounded-bl-xl bg-gradient-to-r from-amber-400 to-fuchsia-500 text-black text-xs font-black">
                <Gem className="w-3.5 h-3.5" /> DIAMOND VIP
              </div>
              <h3 className="font-display text-xl font-bold pr-24 mb-1">{t.name}</h3>
              <p className="text-sm text-gray-400 mb-4">{t.description}</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-black/30 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">Prize Pool</p>
                  <p className={`font-mono font-black text-lg flex items-center justify-center gap-1 ${coins ? "text-amber-400" : "text-emerald-400"}`}>
                    {coins ? <Coins className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}{Number(t.prize_pool).toLocaleString()}
                  </p>
                </div>
                <div className="bg-black/30 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-500 mb-1">Entry</p>
                  <p className={`font-mono font-black text-lg flex items-center justify-center gap-1 ${coins ? "text-amber-300" : ""}`}>
                    {coins ? <Coins className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}{Number(t.entry_fee).toLocaleString()}
                  </p>
                </div>
              </div>
              <Link to="/tournaments" data-testid="vip-event-link"
                className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-opacity duration-200 hover:opacity-90 ${isDiamond ? "bg-gradient-to-r from-amber-400 to-fuchsia-500 text-black" : "bg-white/5 border border-amber-400/30 text-amber-300"}`}>
                {isDiamond ? <>Enter event <ArrowRight className="w-4 h-4" /></> : <><Lock className="w-4 h-4" /> Reach Diamond to enter</>}
              </Link>
            </motion.div>
          );
        })}
        {vipEvents.length === 0 && <p className="text-gray-500">No VIP events running right now — check back soon.</p>}
      </div>

      {/* Tier ladder */}
      <h2 className="font-display text-lg md:text-lg font-bold mb-4 flex items-center gap-2"><Crown className="w-5 h-5 text-amber-300" /> Loyalty tiers</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {(loy?.tiers || []).map((tier, i) => {
          const reached = (loy?.tier_index ?? 0) >= i;
          return (
            <div key={tier.name} data-testid={`vip-tier-${tier.name.toLowerCase()}`}
              className={`rounded-2xl p-4 border text-center ${reached ? "border-amber-400/40 bg-amber-400/5" : "border-white/10 bg-white/5 opacity-70"}`}>
              <div className={`w-10 h-10 mx-auto rounded-xl bg-gradient-to-br ${TIER_STYLE[tier.name]} flex items-center justify-center mb-2`}>
                {tier.name === "Diamond" ? <Gem className="w-5 h-5 text-white" /> : <Crown className="w-5 h-5 text-white" />}
              </div>
              <p className="font-bold">{tier.name}</p>
              <p className="text-xs text-gray-500 mb-1">{tier.min.toLocaleString()} pts</p>
              <p className="text-xs text-amber-300 font-mono">{tier.multiplier}× bonus</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
